import { DEFAULT_LOCALE, LOCALES } from '@aysar/contracts';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * The one i18next instance.
 *
 * i18next replaces P0's hand-rolled `createTranslator`, and the deciding reason
 * is Arabic plurals rather than convenience. The kit's own strings are counted —
 * "{{count}} selected", "+{{count}} more", "showing {{from}}–{{to}} of
 * {{total}}" — and Arabic has six CLDR plural categories where English has two.
 * "2 محدد" is not a rough translation, it is wrong; the dual form is
 * "عنصران محددان". Getting that right means `Intl.PluralRules` plus suffix
 * resolution plus a fallback chain, which is precisely i18next's core. A second
 * lookup path beside it would be the i18n analogue of N22's one-evaluator rule,
 * so `translate.ts` is deleted rather than kept.
 *
 * The language is seeded synchronously from the persisted preference before
 * React mounts, so the first paint is already in the right language and
 * direction. `PreferencesProvider` is the only thing that calls
 * `changeLanguage` afterwards — the data layer reads the locale from the redux
 * slice, never from here, which keeps `baseQueryWithAuth` free of an i18n
 * import.
 */
void i18next.use(initReactI18next).init({
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...LOCALES],
  // `common` is the fallback namespace; every other one is registered at import
  // time by `registerCatalog` (the kit's `uiKit`, the catalog-derived
  // `permissions`, and one per module through the `app.config.tsx` bridge).
  defaultNS: 'common',
  ns: ['common'],
  resources: {},
  // React escapes before it renders; escaping here too would turn an ampersand
  // in an operator's name into `&amp;` on screen.
  interpolation: { escapeValue: false },
  // A missing key must surface as the key, never as `null` — a visible
  // `uiKit.table.empty` is a bug report, a blank cell is silence.
  returnNull: false,
});

export const i18n = i18next;
